import * as React from 'react';
import { getBaseUrl } from '@/redux/modules/config';
import { getFirstSearchItemId } from '@/redux/modules/search/search.selectors';
import { getItemCardImageUrl, isSimilarItemsPage, logoUrl } from '@/utils/urls';
import { getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useLocation } from 'react-router-dom';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import isEqual from 'react-fast-compare';
import PageHeaderMeta from '@liveauctioneers/caterwaul-components/lib/PageHeaderMeta/PageHeaderMeta';
import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';

type Props = {
    page: number;
    searchTerm: string;
};

const SearchPageHeaderMeta = ({ page, searchTerm }: Props) => {
    const { pathname } = useLocation();
    const similarItemsPage = isSimilarItemsPage(pathname);

    const firstItemId = useAppSelector(getFirstSearchItemId);
    const firstItem = useAppSelector((state) => getItemSummary(state, firstItemId));
    const baseUrl = useAppSelector(getBaseUrl);

    const { formatMessage } = useIntl();
    let title = formatMessage(
        { id: similarItemsPage ? 'moreLikeResults' : 'searchTerms' },
        { searchTerm: cleanSearchTerms(searchTerm) }
    );

    if (page > 1) {
        title += formatMessage({ id: 'page_number_and_title' }, { page });
    }

    title = useAddNotificationCountToTitle(title);

    const description = formatMessage({ id: 'searchTermsDescription' }, { terms: cleanSearchTerms(searchTerm, true) });
    const canonical = `${baseUrl}/search`;

    // @ts-expect-error need to fix type if experiment succeeds.
    const { catalogId, imageVersion, itemId, photo, photos, sellerId } = firstItem || {};
    const firstPhoto = photo || photos?.[0] || 0;
    const imageUrl = getItemCardImageUrl(sellerId, catalogId, itemId, firstPhoto, imageVersion) || logoUrl;

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={description}
            imageUrl={imageUrl}
            title={title}
        >
            <meta
                content="noindex, nofollow"
                name="robots"
            />
        </PageHeaderMeta>
    );
};

export default React.memo<Props>(SearchPageHeaderMeta, isEqual);
